import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  DocumentListFilters,
  DocumentProcessingStatus,
  DocumentReviewState,
  DocumentSource,
  ProcessingStep,
} from '../../../interfaces/document';

interface UseDocumentFiltersReturn {
  filters: DocumentListFilters;
  searchDocumentId: string;
  searchCrmId: string;
  searchPatientName: string;
  pagination: {
    page: number;
    limit: number;
  };
  setFilters: (filters: DocumentListFilters) => void;
  setSearchDocumentId: (value: string) => void;
  setSearchCrmId: (value: string) => void;
  setSearchPatientName: (value: string) => void;
  setPagination: (pagination: { page: number; limit: number }) => void;
  handleFilterChange: (key: keyof DocumentListFilters, value: any) => void;
  handleSearchChange: (field: 'documentId' | 'crmId' | 'patientName', value: string) => void;
  resetToPage1: () => void;
}

/**
 * Custom hook to manage document filters and synchronize them with URL params
 */
/**
 * Review states from the URL, keeping only the ones this build knows.
 *
 * Validated rather than cast. The URL is user-editable and outlives a deploy, so a stale
 * bookmark or a state a newer API introduces would otherwise reach the presentation map as an
 * unknown key — which took the whole filter panel down, since `renderValue` indexed it
 * directly. Unknown values are dropped rather than kept: a filter the UI cannot name is one
 * the operator cannot clear.
 */
export const parseReviewStates = (raw: string | null): DocumentReviewState[] | undefined => {
  if (!raw) {
    return undefined;
  }

  const known = new Set<string>(Object.values(DocumentReviewState));
  const parsed = raw.split(',').filter((state): state is DocumentReviewState => known.has(state));

  return parsed.length > 0 ? parsed : undefined;
};

export const useDocumentFilters = (): UseDocumentFiltersReturn => {
  const location = useLocation();
  const history = useHistory();

  // Parse initial state from URL params
  const parseFiltersFromUrl = useCallback((): {
    filters: DocumentListFilters;
    searchDocumentId: string;
    searchCrmId: string;
    searchPatientName: string;
    pagination: { page: number; limit: number };
  } => {
    const params = new URLSearchParams(location.search);

    const filters: DocumentListFilters = {};

    // Parse array filters
    const processingStatuses = params.get('processingStatuses');
    if (processingStatuses)
      filters.processingStatuses = processingStatuses.split(',') as DocumentProcessingStatus[];

    // URL-synced like the rest, so the "needs attention" view survives opening a document and
    // coming back — the brief asks that the filter hold while working through the list.
    const reviewStates = parseReviewStates(params.get('reviewStates'));
    if (reviewStates) filters.reviewStates = reviewStates;

    const processingSteps = params.get('processingSteps');
    if (processingSteps) filters.processingSteps = processingSteps.split(',') as ProcessingStep[];

    const sourceTypes = params.get('sourceTypes');
    if (sourceTypes) filters.sourceTypes = sourceTypes.split(',') as DocumentSource[];

    const documentTypes = params.get('documentTypes');
    if (documentTypes) filters.documentTypes = documentTypes.split(',');

    const receivingClinicIds = params.get('receivingClinicIds');
    if (receivingClinicIds)
      filters.receivingClinicIds = receivingClinicIds.split(',').map((id) => parseInt(id, 10));

    const detectedClinicIds = params.get('detectedClinicIds');
    if (detectedClinicIds)
      filters.detectedClinicIds = detectedClinicIds.split(',').map((id) => parseInt(id, 10));

    // Parse boolean filters
    const hasReferrals = params.get('hasReferrals');
    if (hasReferrals !== null) filters.hasReferrals = hasReferrals === 'true';

    const requiresReview = params.get('requiresReview');
    if (requiresReview !== null) filters.requiresReview = requiresReview === 'true';

    const isProcessed = params.get('isProcessed');
    if (isProcessed !== null) filters.isProcessed = isProcessed === 'true';

    // Parse search fields
    const searchDocumentId = params.get('searchDocumentId') || '';
    const searchCrmId = params.get('searchCrmId') || '';
    const searchPatientName = params.get('searchPatientName') || '';

    // Parse pagination
    const page = parseInt(params.get('page') || '1', 10);
    const limit = parseInt(params.get('limit') || '20', 10);

    return {
      filters,
      searchDocumentId,
      searchCrmId,
      searchPatientName,
      pagination: { page, limit },
    };
  }, [location.search]);

  // Initialize state from URL
  const initialState = parseFiltersFromUrl();
  const [filters, setFilters] = useState<DocumentListFilters>(initialState.filters);
  const [searchDocumentId, setSearchDocumentId] = useState(initialState.searchDocumentId);
  const [searchCrmId, setSearchCrmId] = useState(initialState.searchCrmId);
  const [searchPatientName, setSearchPatientName] = useState(initialState.searchPatientName);
  const [pagination, setPaginationState] = useState(initialState.pagination);

  // Update URL params whenever filters change
  const updateUrlParams = useCallback(
    (
      currentFilters: DocumentListFilters,
      currentSearchDocumentId: string,
      currentSearchCrmId: string,
      currentSearchPatientName: string,
      currentPagination: { page: number; limit: number },
    ) => {
      const params = new URLSearchParams();

      // Add array filters
      if (currentFilters.processingStatuses && currentFilters.processingStatuses.length > 0) {
        params.set('processingStatuses', currentFilters.processingStatuses.join(','));
      }
      if (currentFilters.reviewStates && currentFilters.reviewStates.length > 0) {
        params.set('reviewStates', currentFilters.reviewStates.join(','));
      }
      if (currentFilters.processingSteps && currentFilters.processingSteps.length > 0) {
        params.set('processingSteps', currentFilters.processingSteps.join(','));
      }
      if (currentFilters.sourceTypes && currentFilters.sourceTypes.length > 0) {
        params.set('sourceTypes', currentFilters.sourceTypes.join(','));
      }
      if (currentFilters.documentTypes && currentFilters.documentTypes.length > 0) {
        params.set('documentTypes', currentFilters.documentTypes.join(','));
      }
      if (currentFilters.receivingClinicIds && currentFilters.receivingClinicIds.length > 0) {
        params.set('receivingClinicIds', currentFilters.receivingClinicIds.join(','));
      }
      if (currentFilters.detectedClinicIds && currentFilters.detectedClinicIds.length > 0) {
        params.set('detectedClinicIds', currentFilters.detectedClinicIds.join(','));
      }

      // Add boolean filters
      if (currentFilters.hasReferrals !== undefined) {
        params.set('hasReferrals', String(currentFilters.hasReferrals));
      }
      if (currentFilters.requiresReview !== undefined) {
        params.set('requiresReview', String(currentFilters.requiresReview));
      }
      if (currentFilters.isProcessed !== undefined) {
        params.set('isProcessed', String(currentFilters.isProcessed));
      }

      // Add search fields
      if (currentSearchDocumentId) params.set('searchDocumentId', currentSearchDocumentId);
      if (currentSearchCrmId) params.set('searchCrmId', currentSearchCrmId);
      if (currentSearchPatientName) params.set('searchPatientName', currentSearchPatientName);

      // Add pagination
      params.set('page', String(currentPagination.page));
      params.set('limit', String(currentPagination.limit));

      // Update URL without adding to history if params are the same
      const newSearch = params.toString();
      if (location.search !== `?${newSearch}`) {
        history.replace(`${location.pathname}?${newSearch}`);
      }
    },
    [location.pathname, location.search, history],
  );

  // Sync URL params when state changes
  useEffect(() => {
    updateUrlParams(filters, searchDocumentId, searchCrmId, searchPatientName, pagination);
  }, [filters, searchDocumentId, searchCrmId, searchPatientName, pagination, updateUrlParams]);

  // Handle filter changes
  const handleFilterChange = useCallback((key: keyof DocumentListFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Handle search changes
  const handleSearchChange = useCallback(
    (field: 'documentId' | 'crmId' | 'patientName', value: string) => {
      switch (field) {
        case 'documentId':
          setSearchDocumentId(value);
          break;
        case 'crmId':
          setSearchCrmId(value);
          break;
        case 'patientName':
          setSearchPatientName(value);
          break;
      }
    },
    [],
  );

  // Reset to page 1 (useful when applying filters)
  const resetToPage1 = useCallback(() => {
    setPaginationState((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Custom setPagination that handles both page and limit
  const setPagination = useCallback((newPagination: { page: number; limit: number }) => {
    setPaginationState(newPagination);
  }, []);

  return {
    filters,
    searchDocumentId,
    searchCrmId,
    searchPatientName,
    pagination,
    setFilters,
    setSearchDocumentId,
    setSearchCrmId,
    setSearchPatientName,
    setPagination,
    handleFilterChange,
    handleSearchChange,
    resetToPage1,
  };
};
